import React from 'react';

/**
 * OptimizedImage Component
 * Provides lazy loading, responsive images, and WebP support
 * Includes explicit width/height for better CLS scores
 * Uses aggressive compression for better PageSpeed scores
 */
export const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width = 800,
  height = 600,
  priority = false,
  sizes = '100vw',
  aspectRatio
}) => {
  // Generate optimized URLs for different sizes with aggressive compression
  const getOptimizedUrl = (url, targetWidth, quality = 60) => {
    if (!url) return '';
    
    if (url.includes('unsplash.com')) {
      // Unsplash URL optimization - force WebP and aggressive compression
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?w=${targetWidth}&q=${quality}&fm=webp&fit=crop&auto=format`;
    } else if (url.includes('pexels.com')) {
      // Pexels URL optimization
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?auto=compress&cs=tinysrgb&w=${targetWidth}&dpr=1`;
    }
    return url;
  };

  // Generate srcset for responsive images with smaller sizes
  const generateSrcSet = (url) => {
    if (!url) return '';
    const widths = [320, 480, 640, 800, 1024];
    return widths
      .map(w => `${getOptimizedUrl(url, w, 55)} ${w}w`)
      .join(', ');
  };

  const imgStyle = {
    ...(aspectRatio ? { aspectRatio } : {}),
  };

  if (!src) return null;

  return (
    <img
      src={getOptimizedUrl(src, width > 640 ? 640 : width, 60)}
      srcSet={generateSrcSet(src)}
      sizes={sizes}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : 'auto'}
      style={imgStyle}
    />
  );
};

/**
 * HeroImage Component
 * Specifically optimized for above-the-fold hero images
 * Uses smaller image sizes for faster LCP
 */
export const HeroImage = ({
  src,
  alt,
  className = '',
}) => {
  const getOptimizedUrl = (url, targetWidth) => {
    if (!url) return '';
    if (url.includes('unsplash.com')) {
      const baseUrl = url.split('?')[0];
      // Use WebP and lower quality for hero background
      return `${baseUrl}?w=${targetWidth}&q=70&fm=webp&fit=crop&auto=format`;
    } else if (url.includes('pexels.com')) {
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?auto=compress&cs=tinysrgb&w=${targetWidth}&dpr=1`;
    }
    return url;
  };

  if (!src) return <div className={className} />;

  return (
    <div 
      className={className}
      style={{
        backgroundImage: `url('${getOptimizedUrl(src, 1280)}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    />
  );
};

export default OptimizedImage;
